import React from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import ConfirmContext from 'contexts/ConfirmContext';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from '@apollo/react-hooks';

import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import {
  Typography,
  IconButton,
  TableRow,
  TableHead,
  TableContainer,
  TableCell,
  TableBody,
  Table
} from '@material-ui/core';

import { UPCOMING_ACTIVITIES } from 'graphql/queries/overview';
import { CANCEL_ACTIVITY_BOOKING } from 'graphql/mutations/booking';

import { guestsSumSeats } from 'utils/bookings/prepareBookingsData';

import { EditIcon } from 'components/icons';
import { UpcomingActivitiesDialog } from 'components';

import useStyles from './styles';

const headingsConfig = classes => {
  return [
    {
      label: 'Product code',
      align: 'left',
      cellClassName: classes.productCodeCell
    },
    {
      label: 'Activity name',
      cellClassName: classes.unitCell,
      align: 'left'
    },
    {
      label: 'Activity type',
      cellClassName: classes.activityTypeCell,
      align: 'left'
    },
    {
      label: 'Booked/Maximum available seats',
      align: 'left',
      cellClassName: classes.participantsCell
    },
    {
      label: 'Start time',
      align: 'left',
      cellClassName: classes.startTimeCell
    },
    {
      label: 'Duration',
      align: 'left',
      cellClassName: classes.durationCell
    },
    { label: 'Actions', align: 'right' }
  ];
};

function UpcomingActivities({ ownerId, upcomingPeriod }) {
  const classes = useStyles();
  const history = useHistory();
  const [t] = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const confirm = React.useContext(ConfirmContext);

  const [open, setOpen] = React.useState(false);
  const [dialogContent, setDialogContent] = React.useState({});
  const [cancelActivityBookings] = useMutation(CANCEL_ACTIVITY_BOOKING);
  const { data, refetch } = useQuery(UPCOMING_ACTIVITIES, {
    variables: {
      businessOwnerId: ownerId,
      closestEventsPeriod: upcomingPeriod
    },
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true
  });

  const onClose = isRefetch => {
    setOpen(false);
    if (typeof isRefetch === 'boolean' && isRefetch) refetch();
  };
  const openInfoDialog = booking => {
    setOpen(true);
    setDialogContent(booking);
  };

  const getActivityBookingsGuestsNumber = activity => {
    let guests = 0;

    activity.activityBookings.map(booking => {
      guests += guestsSumSeats(booking.guests);
    });

    return guests;
  };

  const activityCancellationHandler = item => {
    const activityBookings = item.activityBookings.map(booking => {
      return parseInt(booking.id);
    });
    confirm({
      title: `${t('Cancel all Activities')}?`,
      description: `Activity: ${item.title} • ${item.productCode}`,
      confirmationText: t('Cancel all Activities'),
      cancellationText: t('Back')
    }).then(() =>
      cancelActivityBookings({
        variables: { ids: activityBookings }
      }).then(() => {
        enqueueSnackbar(t('Activity was successfully canceled!'), {
          variant: 'success'
        });
        onClose();
        refetch();
      })
    );
  };
  return (
    <TableContainer component="div" classes={{ root: classes.container }}>
      <Table className={classes.table} aria-label="simple table">
        <TableHead>
          <TableRow>
            {headingsConfig(classes).map(heading => (
              <TableCell
                key={heading.label}
                align={heading.align}
                style={{ padding: '20px' }}
                className={classNames(
                  classes.tableCell,
                  classes.heading,
                  heading.cellClassName
                )}
              >
                {t(heading.label)}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data &&
            data.activities &&
            data.activities.map((activity, index) => {
              return (
                <TableRow
                  key={index}
                  classes={{ root: classes.tableRow }}
                  onClick={e => {
                    e.stopPropagation();
                    openInfoDialog(activity);
                  }}
                >
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.productCodeCell
                    )}
                    align="left"
                  >
                    <Typography>{activity.productCode}</Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(classes.tableCell, classes.unitCell)}
                    align="left"
                  >
                    <Typography>
                      {activity.deactivationBooking
                        ? 'Deactivation Booking'
                        : activity.title}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.activityTypeCell
                    )}
                    align="left"
                  >
                    <Typography>
                      {activity.isOpen ? t('Open') : t('Private')}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.participantsCell
                    )}
                    align="left"
                  >
                    <Typography>
                      {getActivityBookingsGuestsNumber(activity)}/
                      {activity.maxParticipants}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.startTimeCell
                    )}
                    align="left"
                  >
                    <Typography>
                      {activity.closestEvent.startDate
                        ? `${activity.closestEvent.startDate}`
                        : '—'}
                      {activity.closestEvent.startTime
                        ? `, ${moment(
                            activity.closestEvent.startTime,
                            'HH:mm:ssZZ'
                          ).format('HH:mm')}`
                        : ''}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classNames(
                      classes.tableCell,
                      classes.durationCell
                    )}
                    align="left"
                  >
                    <Typography>
                      {activity.closestEvent.duration
                        ? activity.closestEvent.duration
                        : '—'}{' '}
                      {t(activity.closestEvent.timeType)}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{ paddingBottom: 5, paddingTop: 5 }}
                    className={classes.tableCell}
                    align="right"
                  >
                    <div
                      style={{ display: 'inline-flex', paddingRight: '10px' }}
                    >
                      <IconButton
                        className={classes.iconButton}
                        disableTouchRipple={true}
                        disableFocusRipple={true}
                        aria-label="edit"
                        onClick={e => {
                          e.stopPropagation();
                          history.push(`/activities/${activity.id}`);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        disableTouchRipple={true}
                        disableFocusRipple={true}
                        className={classes.iconButton}
                        aria-label="delete"
                        onClick={e => {
                          e.stopPropagation();
                          activityCancellationHandler(activity);
                        }}
                      >
                        <HighlightOffIcon className={classes.closeIcon} />
                      </IconButton>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          <UpcomingActivitiesDialog
            activity={dialogContent}
            onClose={onClose}
            open={open}
            onCancel={activityCancellationHandler}
          />
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default UpcomingActivities;
